/**
 * Coups de pouce — Attendu 654 « Milieu d'un segment ».
 *
 * Une entrée par `generator_key` (cf. gen_654.js). Contrat d'une entrée :
 *   pages : 2 pages MAXIMUM, chacune { titre, lignes?, svg? }.
 *           Page 1 « La règle »   : les phrases du mini-cours.
 *           Page 2 « Un exemple » : la figure qui les illustre.
 *
 * Règle absolue : l'aide porte sur d'AUTRES lettres et d'AUTRES valeurs que
 * celles que le générateur peut tirer, sinon elle donne la réponse.
 * Emphase : **texte** (rendu en gras, pas de HTML brut). Pas de tiret cadratin.
 */

// ── Figure Ex. 1 : chaque condition prise seule échoue ──
// Les deux premiers cas SONT les deux pièges du pool de fausses (médiatrice et
// point quelconque du segment) ; le troisième cumule les deux conditions.
// PQ = 7 cm : le générateur ne tire que des valeurs paires de 4 à 18.
const SVG_DEUX_CONDITIONS = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 206" role="img"
     aria-label="Trois cas : M sur le segment mais pas équidistant, M équidistant mais hors du segment, puis M au milieu">
  <line x1="30" y1="32" x2="180" y2="32" stroke="#0f172a" stroke-width="2"/>
  <line x1="30" y1="26" x2="30" y2="38" stroke="#0f172a" stroke-width="2"/>
  <line x1="180" y1="26" x2="180" y2="38" stroke="#0f172a" stroke-width="2"/>
  <circle cx="73" cy="32" r="4" fill="#E24B4A"/>
  <text x="30" y="20" text-anchor="middle" font-size="12" fill="#0f172a">P</text>
  <text x="73" y="20" text-anchor="middle" font-size="12" font-weight="700" fill="#E24B4A">M</text>
  <text x="180" y="20" text-anchor="middle" font-size="12" fill="#0f172a">Q</text>
  <text x="51" y="48" text-anchor="middle" font-size="10" fill="#475569">2 cm</text>
  <text x="126" y="48" text-anchor="middle" font-size="10" fill="#475569">5 cm</text>
  <text x="200" y="30" font-size="17" font-weight="700" fill="#E24B4A">✗</text>
  <text x="218" y="29" font-size="11" fill="#334155">sur [PQ], mais PM ≠ MQ</text>

  <circle cx="105" cy="86" r="4" fill="#E24B4A"/>
  <text x="105" y="76" text-anchor="middle" font-size="12" font-weight="700" fill="#E24B4A">M</text>
  <line x1="105" y1="90" x2="30" y2="112" stroke="#cbd5e1" stroke-width="1" stroke-dasharray="3 3"/>
  <line x1="105" y1="90" x2="180" y2="112" stroke="#cbd5e1" stroke-width="1" stroke-dasharray="3 3"/>
  <line x1="30" y1="112" x2="180" y2="112" stroke="#0f172a" stroke-width="2"/>
  <line x1="30" y1="106" x2="30" y2="118" stroke="#0f172a" stroke-width="2"/>
  <line x1="180" y1="106" x2="180" y2="118" stroke="#0f172a" stroke-width="2"/>
  <text x="30" y="130" text-anchor="middle" font-size="12" fill="#0f172a">P</text>
  <text x="180" y="130" text-anchor="middle" font-size="12" fill="#0f172a">Q</text>
  <text x="60" y="102" text-anchor="middle" font-size="10" fill="#475569">4,5 cm</text>
  <text x="152" y="102" text-anchor="middle" font-size="10" fill="#475569">4,5 cm</text>
  <text x="200" y="110" font-size="17" font-weight="700" fill="#E24B4A">✗</text>
  <text x="218" y="109" font-size="11" fill="#334155">PM = MQ, mais M n'est</text>
  <text x="218" y="123" font-size="11" fill="#334155">pas sur [PQ]</text>

  <line x1="30" y1="176" x2="180" y2="176" stroke="#0f172a" stroke-width="2"/>
  <line x1="30" y1="170" x2="30" y2="182" stroke="#0f172a" stroke-width="2"/>
  <line x1="180" y1="170" x2="180" y2="182" stroke="#0f172a" stroke-width="2"/>
  <circle cx="105" cy="176" r="4" fill="#1D9E75"/>
  <text x="30" y="164" text-anchor="middle" font-size="12" fill="#0f172a">P</text>
  <text x="105" y="164" text-anchor="middle" font-size="12" font-weight="700" fill="#1D9E75">M</text>
  <text x="180" y="164" text-anchor="middle" font-size="12" fill="#0f172a">Q</text>
  <text x="67" y="192" text-anchor="middle" font-size="10" fill="#475569">3,5 cm</text>
  <text x="143" y="192" text-anchor="middle" font-size="10" fill="#475569">3,5 cm</text>
  <text x="200" y="174" font-size="17" font-weight="700" fill="#1D9E75">✓</text>
  <text x="218" y="173" font-size="11" font-weight="700" fill="#0f172a">sur [PQ] ET PM = MQ</text>
</svg>`.trim();

// ── Figure Ex. 2 : diviser d'abord, convertir ensuite ──
// RT = 26 cm : le générateur ne tire que des longueurs de 3 à 18 cm. Les deux
// moitiés sont cotées AVANT le calcul, pour que « divisé par 2 » se voie avant
// de s'écrire. Les deux étapes numérotées reprennent les deux lignes de la règle.
const SVG_DEMI_LONGUEUR = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 176" role="img"
     aria-label="RT mesure 26 cm, M est le milieu, chaque moitié vaut 13 cm soit 130 mm">
  <line x1="40" y1="46" x2="310" y2="46" stroke="#0f172a" stroke-width="2.5"/>
  <line x1="40" y1="39" x2="40" y2="53" stroke="#0f172a" stroke-width="2.5"/>
  <line x1="310" y1="39" x2="310" y2="53" stroke="#0f172a" stroke-width="2.5"/>
  <circle cx="175" cy="46" r="4.5" fill="#F97316"/>
  <text x="40" y="34" text-anchor="middle" font-size="13" fill="#0f172a">R</text>
  <text x="175" y="34" text-anchor="middle" font-size="13" font-weight="700" fill="#F97316">M</text>
  <text x="310" y="34" text-anchor="middle" font-size="13" fill="#0f172a">T</text>
  <text x="107" y="64" text-anchor="middle" font-size="11" font-weight="700" fill="#F97316">13 cm</text>
  <text x="242" y="64" text-anchor="middle" font-size="11" font-weight="700" fill="#F97316">13 cm</text>

  <path d="M40 74 L40 80 L310 80 L310 74" fill="none" stroke="#94a3b8" stroke-width="1.3"/>
  <text x="175" y="96" text-anchor="middle" font-size="12" fill="#475569">26 cm</text>

  <circle cx="62" cy="126" r="9" fill="#FFB938"/>
  <text x="62" y="130" text-anchor="middle" font-size="11" font-weight="700" fill="#3b2303">1</text>
  <text x="82" y="131" font-size="15" font-weight="700" fill="#0f172a">26 ÷ 2 = 13 cm</text>

  <circle cx="62" cy="158" r="9" fill="#FFB938"/>
  <text x="62" y="162" text-anchor="middle" font-size="11" font-weight="700" fill="#3b2303">2</text>
  <text x="82" y="163" font-size="15" font-weight="700" fill="#F97316">13 cm = 130 mm</text>
</svg>`.trim();

// ── Figure Ex. 3 : laquelle des deux conditions échoue ? ──
// Les verdicts reprennent MOT POUR MOT la formulation des options du QCM, pour
// que l'élève fasse le lien figure → réponse. Le cas « oui, c'est le milieu »
// n'est pas repris : il est déjà dans le coup de pouce de l'exo 1, et l'enjeu
// ici est d'identifier LAQUELLE des deux conditions manque.
// PQ = 20 cm et PM = 13 cm : hors des plages du générateur (AB ≤ 12, MA ≤ 9).
const SVG_QUELLE_CONDITION = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 176" role="img"
     aria-label="Deux cas d'échec : M sur le segment mais pas équidistant, puis M équidistant mais hors du segment">
  <line x1="30" y1="34" x2="230" y2="34" stroke="#0f172a" stroke-width="2"/>
  <line x1="30" y1="28" x2="30" y2="40" stroke="#0f172a" stroke-width="2"/>
  <line x1="230" y1="28" x2="230" y2="40" stroke="#0f172a" stroke-width="2"/>
  <circle cx="110" cy="34" r="4" fill="#E24B4A"/>
  <text x="30" y="22" text-anchor="middle" font-size="12" fill="#0f172a">P</text>
  <text x="110" y="22" text-anchor="middle" font-size="12" font-weight="700" fill="#E24B4A">M</text>
  <text x="230" y="22" text-anchor="middle" font-size="12" fill="#0f172a">Q</text>
  <text x="70" y="52" text-anchor="middle" font-size="11" fill="#475569">8 cm</text>
  <text x="170" y="52" text-anchor="middle" font-size="11" fill="#475569">12 cm</text>
  <text x="30" y="74" font-size="15" font-weight="700" fill="#E24B4A">✗</text>
  <text x="48" y="73" font-size="11.5" fill="#334155">M est sur [PQ] mais pas équidistant</text>

  <circle cx="130" cy="104" r="4" fill="#E24B4A"/>
  <text x="130" y="94" text-anchor="middle" font-size="12" font-weight="700" fill="#E24B4A">M</text>
  <line x1="130" y1="108" x2="30" y2="132" stroke="#cbd5e1" stroke-width="1" stroke-dasharray="3 3"/>
  <line x1="130" y1="108" x2="230" y2="132" stroke="#cbd5e1" stroke-width="1" stroke-dasharray="3 3"/>
  <text x="66" y="112" text-anchor="middle" font-size="11" fill="#475569">13 cm</text>
  <text x="194" y="112" text-anchor="middle" font-size="11" fill="#475569">13 cm</text>
  <line x1="30" y1="132" x2="230" y2="132" stroke="#0f172a" stroke-width="2"/>
  <line x1="30" y1="126" x2="30" y2="138" stroke="#0f172a" stroke-width="2"/>
  <line x1="230" y1="126" x2="230" y2="138" stroke="#0f172a" stroke-width="2"/>
  <text x="30" y="150" text-anchor="middle" font-size="12" fill="#0f172a">P</text>
  <text x="230" y="150" text-anchor="middle" font-size="12" fill="#0f172a">Q</text>
  <text x="30" y="170" font-size="15" font-weight="700" fill="#E24B4A">✗</text>
  <text x="48" y="169" font-size="11.5" fill="#334155">M est équidistant mais n'appartient pas à [PQ]</text>
</svg>`.trim();

// ── Figure Ex. 4 : la convention du codage, et rien d'autre ──
// L'exemple n'illustre PAS une situation de milieu : montrer un M bien placé
// à côté d'un M mal placé revient à afficher la réponse de l'exercice. Il
// enseigne uniquement à LIRE le codage ; l'élève applique ensuite lui-même les
// deux conditions de la page 1 à ses quatre figures.
//
// Les deux segments ont exactement la même longueur (110 px) mais des
// inclinaisons différentes : l'œil hésite, le codage tranche. Une figure où
// le codage contredirait le dessin serait fausse — on ne s'y autorise pas.
const SVG_LIRE_CODAGE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 128" role="img"
     aria-label="Deux segments de même longueur mais d'inclinaisons différentes, portant le même petit trait">
  <line x1="40" y1="50" x2="150" y2="50" stroke="#0f172a" stroke-width="2.5"/>
  <line x1="40" y1="44" x2="40" y2="56" stroke="#0f172a" stroke-width="2"/>
  <line x1="150" y1="44" x2="150" y2="56" stroke="#0f172a" stroke-width="2"/>
  <line x1="95" y1="44.5" x2="95" y2="55.5" stroke="#E24B4A" stroke-width="2"/>

  <line x1="210" y1="90" x2="294" y2="19" stroke="#0f172a" stroke-width="2.5"/>
  <line x1="206.8" y1="86.2" x2="213.2" y2="93.8" stroke="#0f172a" stroke-width="2"/>
  <line x1="290.8" y1="15.2" x2="297.2" y2="22.8" stroke="#0f172a" stroke-width="2"/>
  <line x1="248.5" y1="50.4" x2="255.5" y2="58.6" stroke="#E24B4A" stroke-width="2"/>

  <text x="180" y="118" text-anchor="middle" font-size="13" font-weight="700" fill="#E24B4A">Même petit trait = même longueur</text>
</svg>`.trim();

const CP_654 = {
    definition_vf_tableau: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    'Le milieu doit remplir **deux conditions en même temps** : être **sur** le segment, et être à **égale distance** des deux extrémités.',
                    'Si une seule des deux est vraie, ce n\'est pas le milieu.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_DEUX_CONDITIONS },
        ],
    },

    demilongueur_saisie: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    'Le milieu coupe le segment en **deux parts égales** : chaque moitié vaut la longueur totale **divisée par 2**.',
                    'Si la réponse est demandée en **mm**, divise d\'abord, convertis ensuite (1 cm = 10 mm).',
                ],
            },
            { titre: 'Un exemple', svg: SVG_DEMI_LONGUEUR },
        ],
    },

    reconnaitre_milieu_qcm_unique: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    'Vérifie les **deux conditions** l\'une après l\'autre : M est-il **sur** le segment ? Est-il à **égale distance** des deux extrémités ?',
                    'Si une condition est fausse, choisis la réponse qui indique laquelle.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_QUELLE_CONDITION },
        ],
    },

    placement_milieu_qcm_images: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    'Le milieu d\'un segment est le point qui est **sur** ce segment et à **égale distance** des deux extrémités.',
                    'Sur la figure, deux longueurs marquées du **même petit trait** sont égales.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_LIRE_CODAGE },
        ],
    },
};

export default CP_654;
